// ─────────────────────────────────────────────────────────────────────────────
// One color per phase of work.
//
// Every pastel means exactly one thing, everywhere in the app: a lavender card,
// chip, dot or lane is always a lead; butter is always money we've billed and
// are waiting on. Before this module the Work board, StatusChip, StatusBadge
// and workStage.stageTone each kept their own map and disagreed (a lead was
// sky on the board and mint on a badge; paid was mint on the board and sky on
// a chip), so color carried no meaning.
//
//   sales       lavender   lead, qualified, proposal, negotiation
//   quote       peach      quoted (sent, waiting on the customer)
//   production  sky        accepted, scheduled, in progress
//   billing     butter     completed (ready to bill), invoiced
//   paid        mint       paid, won
//   late        blush      overdue / past due — overrides the phase
//
// Anything terminal or not-yet-real (draft, cancelled, lost, archived) is
// neutral glass rather than a color.
// ─────────────────────────────────────────────────────────────────────────────

export type PhaseTone = 'lavender' | 'peach' | 'sky' | 'butter' | 'mint' | 'blush'
export type Phase = 'sales' | 'quote' | 'production' | 'billing' | 'paid' | 'late'

export const PHASE_TONE: Record<Phase, PhaseTone> = {
  sales: 'lavender',
  quote: 'peach',
  production: 'sky',
  billing: 'butter',
  paid: 'mint',
  late: 'blush',
}

/** Base pastel hex per tone (same values as the --<tone> CSS tokens) — for
 *  inline styles and SVG fills that can't read a CSS variable. */
export const TONE_HEX: Record<PhaseTone, string> = {
  mint: '#A8E6CE',
  peach: '#FFC5A0',
  lavender: '#C7B9F0',
  butter: '#F3DF93',
  blush: '#F4B5BD',
  sky: '#B5D3F0',
}

/** Deep text/icon color that sits on each pastel (the --<tone>-deep tokens). */
export const TONE_DEEP_HEX: Record<PhaseTone, string> = {
  mint: '#0F4A2E',
  peach: '#4A2108',
  lavender: '#2A1A5C',
  butter: '#3D2E08',
  blush: '#511622',
  sky: '#0F2A4D',
}

export const PHASE_LABEL: Record<Phase, string> = {
  sales: 'Leads',
  quote: 'Quotes',
  production: 'Jobs',
  billing: 'Invoices',
  paid: 'Paid',
  late: 'Overdue',
}

/** Which document a status string belongs to — only needed for statuses
 *  shared between documents ("sent" is a quote stage and an invoice stage). */
export type StatusKind = 'deal' | 'quote' | 'job' | 'invoice'

const STATUS_PHASE: Record<string, Phase> = {
  lead: 'sales',
  new: 'sales',
  qualified: 'sales',
  proposal: 'sales',
  negotiation: 'sales',
  quoted: 'quote',
  accepted: 'production',
  scheduled: 'production',
  in_progress: 'production',
  active: 'production',
  completed: 'billing',
  invoiced: 'billing',
  partially_paid: 'billing',
  partial: 'billing',
  paid: 'paid',
  won: 'paid',
  overdue: 'late',
  late: 'late',
}

// Statuses whose meaning depends on the document they sit on.
const KIND_PHASE: Record<StatusKind, Record<string, Phase>> = {
  deal: {},
  quote: { sent: 'quote', viewed: 'quote', pending: 'quote' },
  job: {},
  invoice: { sent: 'billing', viewed: 'billing', pending: 'billing' },
}

/** Normalizes 'IN_PROGRESS', 'in-progress' and 'In Progress' to 'in_progress'. */
function normalize(status: string): string {
  return status.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

export function phaseOfStatus(status: string | null | undefined, kind?: StatusKind): Phase | null {
  if (!status) return null
  const key = normalize(status)
  return (kind && KIND_PHASE[kind][key]) || STATUS_PHASE[key] || null
}

/** The pastel for a stage or status, or 'neutral' when it has no phase. */
export function toneForStatus(
  status: string | null | undefined,
  kind?: StatusKind,
): PhaseTone | 'neutral' {
  const phase = phaseOfStatus(status, kind)
  return phase ? PHASE_TONE[phase] : 'neutral'
}
